import { api, type ApiPath } from "@/lib/http.ts";
import type { RoutineStateResp, UpdateRoutineStateReq } from "@/types/routines.ts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Reads and writes for a routine's state file.
 *
 * Keyed on the full path rather than a routine id, because the same file is
 * reachable through two routers — `/api/a/routines/:routineId/state` from the
 * app and `/api/s/routines/:accountId/:routineId/state` from the admin service.
 * Taking the finished path also keeps `ApiPath` doing its job: a caller writes
 * the literal `/api/a/...` prefix inline so the template literal still satisfies
 * the type, where interpolating an opaque prefix here would have forced a cast
 * and thrown away the check that catches a missing mount.
 */
export const routineStateQueries = {
  all: () => ["routine-state"] as const,
  detail: (statePath: ApiPath) =>
    queryOptions({
      queryKey: [...routineStateQueries.all(), statePath] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<RoutineStateResp>(statePath, { signal });
        return response.data;
      },
    }),
};

export const useSaveRoutineState = (statePath: ApiPath) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const response = await api.post<RoutineStateResp, UpdateRoutineStateReq>(statePath, {
        content,
      });
      return response.data;
    },
    // The POST returns the saved file, so seed the cache from it rather than
    // invalidating: a refetch would race the operator's next keystroke.
    onSuccess: (data) => {
      queryClient.setQueryData(routineStateQueries.detail(statePath).queryKey, data);
    },
  });
};
